/**
 * ProfilePage — profiel bewerken (spec §8.8).
 *
 * - Display name, locale and timezone save through
 *   repositories.profile.upsertMyProfile (server-confirmed, SyncStatus truth;
 *   role is never editable here — the server profile stays authoritative).
 * - Avatar uploads go to the public-read/owner-write 'avatars' bucket via
 *   avatarUpload.ts; the profile row stores the object path.
 * - SUPABASE_NOT_CONFIGURED: honest Banner — no silent local profile edits
 *   that would diverge from the server profile.
 */
import { useEffect, useRef, useState } from 'react'
import {
  Banner,
  Button,
  Card,
  Select,
  SyncStatus,
  TextField,
  useToast,
} from '@/components/ui'
import { UserIcon } from '@/components/icons'
import { PageHeader } from '@/components/navigation'
import { useAuth } from '@/features/auth'
import { validateDisplayName } from '@/features/auth/validation'
import { repositories } from '@/services/repositories'
import { isCloudConfigured } from '@/lib/supabase/client'
import { toAppError } from '@/lib/supabase/errors'
import { PATHS } from '@/app/router/paths'
import type { AppError, SyncSaveState } from '@/types'
import { getAvatarPublicUrl, uploadAvatar, validateAvatarFile } from './avatarUpload'
import './settings.css'

const LOCALE_OPTIONS = [
  { value: 'nl-NL', label: 'Nederlands (Nederland)' },
  { value: 'nl-BE', label: 'Nederlands (België)' },
  { value: 'en-GB', label: 'Engels (VK)' },
  { value: 'en-US', label: 'Engels (VS)' },
  { value: 'de-DE', label: 'Duits' },
  { value: 'fr-FR', label: 'Frans' },
]

const TIMEZONE_OPTIONS = [
  { value: 'Europe/Amsterdam', label: 'Amsterdam' },
  { value: 'Europe/Brussels', label: 'Brussel' },
  { value: 'Europe/London', label: 'Londen' },
  { value: 'Europe/Berlin', label: 'Berlijn' },
  { value: 'Europe/Paris', label: 'Parijs' },
  { value: 'UTC', label: 'UTC' },
]

function optionList(base: { value: string; label: string }[], current: string): { value: string; label: string }[] {
  if (base.some((option) => option.value === current)) return base
  return [...base, { value: current, label: current }]
}

export default function ProfilePage() {
  const { userId, email, profile, refreshProfile } = useAuth()
  const { showToast } = useToast()

  const [displayName, setDisplayName] = useState(profile?.displayName ?? '')
  const [locale, setLocale] = useState(profile?.locale ?? 'nl-NL')
  const [timezone, setTimezone] = useState(
    profile?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Europe/Amsterdam',
  )
  const [nameError, setNameError] = useState<string | null>(null)
  const [saveState, setSaveState] = useState<SyncSaveState | null>(null)
  const [saveError, setSaveError] = useState<AppError | null>(null)
  const [saving, setSaving] = useState(false)

  const [avatarVersion, setAvatarVersion] = useState<number>(0)
  const [avatarError, setAvatarError] = useState<AppError | null>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Hydrate once the server profile arrives (RequireAuth loads it async).
  useEffect(() => {
    if (!profile) return
    setDisplayName((current) => (current === '' ? (profile.displayName ?? '') : current))
    setLocale((current) => (current === 'nl-NL' ? profile.locale || current : current))
    setTimezone((current) => current || profile.timezone)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id])

  const cloud = isCloudConfigured()
  const avatarUrl = getAvatarPublicUrl(profile?.avatarPath ?? null, avatarVersion || undefined)
  const dirty =
    profile !== null &&
    (displayName.trim() !== (profile.displayName ?? '') ||
      locale !== profile.locale ||
      timezone !== profile.timezone)

  const handleSave = async () => {
    const validationError = validateDisplayName(displayName)
    setNameError(validationError)
    if (validationError) return
    setSaving(true)
    setSaveError(null)
    setSaveState('syncing')
    try {
      await repositories.profile.upsertMyProfile({
        displayName: displayName.trim(),
        locale,
        timezone,
      })
      await refreshProfile()
      setSaveState('cloud_saved')
      showToast({ message: 'Je profiel is opgeslagen.', tone: 'success' })
    } catch (error) {
      const appError = toAppError(error, 'database')
      setSaveError(appError)
      setSaveState('sync_failed')
    } finally {
      setSaving(false)
    }
  }

  const handleAvatarFile = async (file: File | undefined) => {
    if (!file || !userId) return
    const invalid = validateAvatarFile(file)
    if (invalid) {
      setAvatarError(invalid)
      return
    }
    setAvatarUploading(true)
    setAvatarError(null)
    try {
      const path = await uploadAvatar(userId, file)
      await repositories.profile.upsertMyProfile({ avatarPath: path })
      await refreshProfile()
      setAvatarVersion(Date.now())
      showToast({ message: 'Je profielfoto is bijgewerkt.', tone: 'success' })
    } catch (error) {
      setAvatarError(toAppError(error, 'storage'))
    } finally {
      setAvatarUploading(false)
    }
  }

  return (
    <div className="settings-page">
      <PageHeader
        title="Profiel"
        subtitle={email ?? undefined}
        backTo={PATHS.app.more}
      />

      {!cloud && (
        <Banner tone="warning">
          De cloudverbinding is niet ingesteld. Profielwijzigingen kunnen nu niet worden opgeslagen;
          je gegevens blijven ongewijzigd bewaard.
        </Banner>
      )}

      {/* Avatar */}
      <section className="settings-section" aria-label="Profielfoto">
        <h2 className="settings-section__title">Profielfoto</h2>
        <Card>
          <div className="avatar-editor">
            {avatarUrl ? (
              <img className="avatar-editor__preview" src={avatarUrl} alt="Je huidige profielfoto" />
            ) : (
              <span className="avatar-editor__preview" aria-hidden="true">
                <UserIcon size={40} />
              </span>
            )}
            <div className="avatar-editor__actions">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                hidden
                onChange={(event) => {
                  void handleAvatarFile(event.target.files?.[0])
                  event.target.value = ''
                }}
              />
              <Button
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                loading={avatarUploading}
                disabled={!cloud || !userId}
              >
                {avatarUrl ? 'Andere foto kiezen' : 'Foto kiezen'}
              </Button>
              <p className="avatar-editor__hint">jpg, png, webp of gif — maximaal 2 MB.</p>
            </div>
          </div>
          {avatarError && (
            <p className="settings-inline-error" role="alert">
              {avatarError.userMessage}
            </p>
          )}
        </Card>
      </section>

      {/* Details */}
      <section className="settings-section" aria-label="Gegevens">
        <h2 className="settings-section__title">Gegevens</h2>
        <Card>
          <div className="settings-stack">
            <TextField
              label="Naam"
              value={displayName}
              onChange={(value) => {
                setDisplayName(value)
                if (nameError) setNameError(validateDisplayName(value))
              }}
              error={nameError ?? undefined}
              required
              autoComplete="name"
              disabled={!cloud || saving}
            />
            <Select
              label="Taal"
              value={locale}
              onChange={setLocale}
              options={optionList(LOCALE_OPTIONS, locale)}
            />
            <Select
              label="Tijdzone"
              value={timezone}
              onChange={setTimezone}
              options={optionList(TIMEZONE_OPTIONS, timezone)}
            />
            <div className="settings-sync-row" aria-live="polite">
              <SyncStatus state={saveState} />
            </div>
            {saveError && (
              <p className="settings-inline-error" role="alert">
                {saveError.userMessage}
              </p>
            )}
            <Button
              onClick={() => void handleSave()}
              loading={saving}
              disabled={!cloud || !dirty}
              fullWidth
            >
              Opslaan
            </Button>
          </div>
        </Card>
      </section>
    </div>
  )
}
